import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LuArrowLeft, LuCamera, LuCheck, LuLoaderCircle, LuSearch, LuTrash2, LuVideo, LuX } from "react-icons/lu";
import { getNames } from "country-list";
import { get, ref, update } from "firebase/database";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { auth, database, storage } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import AppPage from "../components/AppPage";
import ModalSheet from "../components/ModalSheet";
import ImageCropModal from "../components/ImageCropModal";
import Avatar from "../components/UserAvatar";
import { NICHES, call as webCall } from "../utils/appData";
import { profileNiches } from "../utils/discoverData";
import { resizeImage, uploadFileToBunny } from "../utils/utils";

const ABOUT_LIMIT = 160;
const COUNTRIES = getNames().sort();
const inputClass = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 outline-none placeholder:text-slate-400 focus:border-[#327691] focus:ring-2 focus:ring-[#327691]/20 disabled:opacity-60 dark:border-[#363e46] dark:bg-[#23272c] dark:text-white";
const labelClass = "mb-2 block text-sm font-semibold text-slate-500 dark:text-slate-400";
const actionClass = "inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-[#327691] px-5 py-2.5 text-sm font-bold text-white disabled:cursor-wait disabled:opacity-50";

function usePreview(file) {
    const [url, setUrl] = useState("");
    useEffect(() => {
        if (!file) { setUrl(""); return; }
        const next = URL.createObjectURL(file);
        setUrl(next);
        return () => URL.revokeObjectURL(next);
    }, [file]);
    return url;
}
function Picture({ url, user, className }) {
    return url ? <img src={url} alt="Profile preview" className={className} /> : <Avatar user={user} className={className} />;
}

export default function EditProfile() {
    const { currentUser, refreshUser } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const uid = currentUser?.uid;
    const completing = Boolean(location.state?.isNewGoogleUser || currentUser?.requiresProfileCompletion);
    const [form, setForm] = useState({ name: "", about: "", website: "", niches: [], country: "", location: "" });
    const [profile, setProfile] = useState(null);
    const [initializing, setInitializing] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    const [photo, setPhoto] = useState(null);
    const [cropFile, setCropFile] = useState(null);
    const [viewPhoto, setViewPhoto] = useState(false);
    const [video, setVideo] = useState(null);
    const [progress, setProgress] = useState(0);
    const [picker, setPicker] = useState("");
    const [search, setSearch] = useState("");
    const photoInput = useRef(null);
    const videoInput = useRef(null);
    const photoUrl = usePreview(photo);
    const cropUrl = usePreview(cropFile);
    const videoUrl = usePreview(video);
    const currentUserRef = useRef(currentUser);
    currentUserRef.current = currentUser;

    useEffect(() => {
        let active = true;
        setInitializing(true);
        setLoadError("");
        setPhoto(null);
        setVideo(null);
        setPicker("");
        setViewPhoto(false);
        setCropFile(null);
        setError("");
        if (!uid) { setInitializing(false); return; }
        get(ref(database, `Users/${uid}`)).then(snapshot => {
            if (!active) return;
            if (!snapshot.exists()) throw new Error("Your profile could not be found.");
            const data = { ...currentUserRef.current, ...snapshot.val(), uid };
            setProfile(data);
            setForm({
                name: data.name || data.displayName || "",
                // About is a UI label; about remains the shared mobile/web field.
                about: String(data.about || "").slice(0, ABOUT_LIMIT),
                website: data.website || "",
                niches: profileNiches(data),
                country: data.country || "",
                location: data.location || "",
            });
        }).catch(err => {
            if (active) setLoadError(err.message || "Could not load your profile.");
        }).finally(() => { if (active) setInitializing(false); });
        return () => { active = false; };
    }, [uid]);

    const change = (key, value) => setForm(previous => ({ ...previous, [key]: value }));
    const openPicker = value => { setSearch(""); setPicker(value); };
    const checkUser = () => {
        if (!uid || auth.currentUser?.uid !== uid) throw new Error("Please sign in again before saving your profile.");
    };
    const handleSave = async event => {
        event?.preventDefault();
        if (busyRef.current || initializing || loadError) return;
        setError("");
        if (!form.name.trim()) { setError("Please enter your full name."); return; }
        if (completing && (!form.niches.length || !form.country)) {
            setError("Please choose your main niche and country to finish your profile."); return;
        }
        let website = form.website.trim();
        if (website) {
            if (!/^https?:\/\//i.test(website)) website = `https://${website}`;
            try {
                const parsed = new URL(website);
                if (!["http:", "https:"].includes(parsed.protocol) || !parsed.hostname.includes(".")) throw new Error();
            } catch { setError("Please enter a valid website address."); return; }
        }
        busyRef.current = true;
        setBusy(true);
        setProgress(0);
        try {
            checkUser();
            const updates = {
                name: form.name.trim(), displayName: form.name.trim(),
                about: form.about.slice(0, ABOUT_LIMIT), website,
                niche: form.niches[0] || "", niches: form.niches,
                country: form.country, location: form.location.trim(),
                requiresProfileCompletion: null,
            };
            if (photo) {
                const picture = storageRef(storage, `profiles/${uid}/avatar_${Date.now()}.webp`);
                await uploadBytes(picture, photo, { contentType: photo.type || "image/webp" });
                const url = await getDownloadURL(picture);
                updates.profilePic = url;
                updates.photoURL = url;
            }
            if (video) {
                const result = await uploadFileToBunny(video, `showreels_${uid}`, video.name, setProgress);
                if (!result.success || !result.playbackUrl) throw new Error(result.message || "Video upload failed.");
                updates.video = result.playbackUrl;
                updates.videoOrientation = result.videoOrientation || "landscape";
                updates.videoWidth = result.videoWidth || null;
                updates.videoHeight = result.videoHeight || null;
                updates.portfolioBanner = "";
            }
            checkUser();
            await update(ref(database, `Users/${uid}`), updates);
            await refreshUser();
            window.showToast?.("Profile updated successfully!", "success");
            navigate("/profile", { replace: true });
        } catch (err) {
            setError(err.message || "Could not save your profile. Please try again.");
        } finally { busyRef.current = false; setBusy(false); }
    };
    const removeVideo = async () => {
        if (busyRef.current) return;
        if (video) { setVideo(null); return; }
        if (!profile?.video || !window.confirm("Remove your showreel video?")) return;
        busyRef.current = true;
        setBusy(true);
        setError("");
        try {
            checkUser();
            const videoId = String(profile.video).split(/[/?#]/).find(part => /^[a-f0-9-]{36}$/i.test(part));
            if (!videoId) throw new Error("Could not identify this showreel. Please contact support.");
            await webCall("deleteWebVideo", { videoId });
            checkUser();
            await update(ref(database, `Users/${uid}`), {
                video: "", videoOrientation: null, videoWidth: null, videoHeight: null,
            });
            setProfile(previous => ({ ...previous, video: "" }));
            await refreshUser();
        } catch (err) { setError(err.message || "Could not remove the video."); }
        finally { busyRef.current = false; setBusy(false); }
    };
    const pickPhoto = event => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/") && !/\.(heic|heif)$/i.test(file.name)) {
            setError("Please choose an image."); return;
        }
        setCropFile(file);
    };
    const items = picker === "country" ? COUNTRIES
        : picker === "extra" ? NICHES.filter(item => !form.niches.includes(item)) : NICHES;
    const selectItem = value => {
        if (picker === "country") change("country", value);
        else if (picker === "main") change("niches", [value, ...form.niches.slice(1).filter(item => item !== value)].slice(0, 3));
        else setForm(previous => ({ ...previous, niches: [...new Set([...previous.niches, value])].slice(0, 3) }));
        setPicker("");
    };
    const saveButton = (
        <button type="submit" form="edit-profile-form" disabled={busy || initializing || Boolean(loadError) || !uid}
            aria-label={busy ? "Saving profile" : "Save profile"} className="grid h-11 w-11 place-items-center rounded-full text-[#327691] hover:bg-[#327691]/10 disabled:opacity-50 dark:text-[#5FAFCB]">
            {busy ? <LuLoaderCircle size={25} className="animate-spin" /> : <LuCheck size={28} />}
        </button>
    );
    return (
        <AppPage title={completing ? "Complete Your Profile" : "Edit Profile"} headerActions={saveButton} hideMobileHeading>
            <form id="edit-profile-form" onSubmit={handleSave} className="mx-auto w-full max-w-[620px] pb-4">
                <div className="mb-6 hidden items-center gap-3 md:flex">
                    <button type="button" onClick={() => navigate(-1)} aria-label="Back" className="grid h-10 w-10 place-items-center rounded-full bg-white dark:bg-[#23272c]"><LuArrowLeft size={22} /></button>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Your profile details</p>
                </div>
                {initializing ? <p role="status" className="py-10 text-center">Loading profile…</p>
                    : loadError ? <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-700 dark:bg-red-950 dark:text-red-200">{loadError} Reload the page to try again.</p>
                        : !uid ? <p role="alert">Please sign in to edit your profile.</p> : <>
                            {completing && <p className="mb-6 rounded-xl bg-[#327691]/10 p-4 text-sm text-[#327691] dark:text-[#5FAFCB]">Almost there! Add your details to finish creating your account.</p>}
                            <fieldset disabled={busy} className="min-w-0 space-y-5">
                                <div className="mb-8 flex justify-center">
                                    <div className="relative">
                                        <button type="button" onClick={() => setViewPhoto(true)} aria-label="View profile photo">
                                            <Picture url={photoUrl} user={profile} className="h-[100px] w-[100px] rounded-full object-cover" />
                                        </button>
                                        <button type="button" onClick={() => photoInput.current?.click()} aria-label="Change profile photo" className="absolute bottom-0 right-0 grid h-9 w-9 place-items-center rounded-full border-[3px] border-[#f6f8fa] bg-[#327691] text-white dark:border-[#181c20]"><LuCamera size={18} /></button>
                                        <input ref={photoInput} type="file" accept="image/*,.heic,.heif" onChange={pickPhoto} className="hidden" />
                                    </div>
                                </div>
                                <div><label htmlFor="profile-name" className={labelClass}>Full Name</label><input id="profile-name" autoComplete="name" value={form.name} onChange={e => change("name", e.target.value)} placeholder="Enter your name" required className={inputClass} /></div>
                                <div>
                                    <div className="mb-2 flex flex-wrap items-center justify-between gap-1"><label htmlFor="profile-about" className="text-sm font-semibold text-slate-500 dark:text-slate-400">About (Max {ABOUT_LIMIT} characters)</label><span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{form.about.length}/{ABOUT_LIMIT}</span></div>
                                    <textarea id="profile-about" value={form.about} maxLength={ABOUT_LIMIT} onChange={e => change("about", e.target.value.slice(0, ABOUT_LIMIT))} placeholder="A short description about you" rows={3} className={inputClass + " resize-y"} />
                                </div>
                                <div><label htmlFor="profile-website" className={labelClass}>Website</label><input id="profile-website" inputMode="url" autoCapitalize="none" value={form.website} onChange={e => change("website", e.target.value)} placeholder="https://yourwebsite.com" className={inputClass} /></div>
                                <div><label id="main-niche-label" className={labelClass}>Main niche</label><button type="button" aria-labelledby="main-niche-label" onClick={() => openPicker("main")} className={inputClass + " flex items-center justify-between gap-3 text-left"}><span>{form.niches[0] || "Select your main niche"}</span><LuSearch size={18} className="shrink-0 text-slate-400" /></button></div>
                                <div>
                                    <p className={labelClass}>Also skilled in (up to 2 more)</p>
                                    <div className="flex flex-wrap gap-2">
                                        {form.niches.slice(1).map(niche => <button key={niche} type="button" onClick={() => change("niches", form.niches.filter(item => item !== niche))} aria-label={`Remove ${niche}`} className="inline-flex items-center gap-2 rounded-full bg-[#327691]/10 px-3 py-2 text-sm font-semibold text-[#327691] dark:text-[#5FAFCB]">{niche}<LuX size={14} /></button>)}
                                        {form.niches.length > 0 && form.niches.length < 3 && <button type="button" onClick={() => openPicker("extra")} className="rounded-full border border-dashed border-[#327691] px-4 py-2 text-sm font-bold text-[#327691] dark:text-[#5FAFCB]">+ Add</button>}
                                    </div>
                                    {!form.niches.length && <p className="text-xs text-slate-500">Pick a main niche first, then add up to two more.</p>}
                                </div>
                                <div><label id="country-label" className={labelClass}>Country</label><button type="button" aria-labelledby="country-label" onClick={() => openPicker("country")} className={inputClass + " flex items-center justify-between gap-3 text-left"}><span>{form.country || "Select your country"}</span><LuSearch size={18} className="shrink-0 text-slate-400" /></button></div>
                                <div><label htmlFor="profile-city" className={labelClass}>City</label><input id="profile-city" autoComplete="address-level2" value={form.location} onChange={e => change("location", e.target.value)} placeholder="Cape Town" className={inputClass} /></div>
                                <div>
                                    <p className={labelClass}>Showreel Video</p>
                                    <input ref={videoInput} type="file" accept="video/*,.mov" className="hidden" onChange={e => { const file = e.target.files?.[0]; e.target.value = ""; if (file) { if (file.type.startsWith("video/") || /\.(mp4|mov|m4v|webm)$/i.test(file.name)) { setVideo(file); setProgress(0); } else setError("Please choose a video file."); } }} />
                                    <button type="button" onClick={() => videoInput.current?.click()} className={`flex min-h-[120px] w-full flex-col items-center justify-center gap-2 rounded-xl border-2 bg-white p-5 text-[#327691] dark:bg-[#23272c] dark:text-[#5FAFCB] ${video || profile?.video ? "border-solid border-[#327691]/40" : "border-dashed border-slate-300 dark:border-slate-600"}`}>
                                        <LuVideo size={28} /><span className="max-w-full break-all text-sm font-semibold">{video?.name || (profile?.video ? "Showreel Video Loaded" : "Select Video")}</span>
                                        {(video || profile?.video) && <span className="text-xs text-slate-500">Tap to replace</span>}
                                    </button>
                                    {videoUrl && <video src={videoUrl} controls preload="metadata" className="mt-3 max-h-64 w-full rounded-xl bg-black" />}
                                    {(video || profile?.video) && <button type="button" onClick={removeVideo} className="mt-3 inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-red-600 dark:text-red-400"><LuTrash2 size={16} />{video ? "Discard selected video" : "Remove Video"}</button>}
                                </div>
                            </fieldset>
                            {error && <p role="alert" className="mt-5 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200">{error}</p>}
                            {busy && progress > 0 && <p role="status" className="mt-4 text-sm text-[#327691] dark:text-[#5FAFCB]">Uploading video… {Math.round(progress)}%</p>}
                            <button type="submit" disabled={busy} className={actionClass + " mt-7 w-full"}>{busy && <LuLoaderCircle className="animate-spin" size={18} />}{busy ? "Saving…" : completing ? "Complete Profile" : "Save Profile"}</button>
                        </>}
            </form>
            <ModalSheet open={Boolean(picker)} onClose={() => setPicker("")} title={picker === "country" ? "Select Country" : picker === "extra" ? "Add niche" : "Main niche"}>
                <div className="relative mb-4"><LuSearch size={18} className="absolute left-3 top-3.5 text-slate-400" /><input aria-label="Search options" value={search} onChange={e => setSearch(e.target.value)} placeholder={picker === "country" ? "Search countries…" : "Search niches…"} className={inputClass + " pl-10"} /></div>
                <div className="space-y-1">
                    {items.filter(item => item.toLowerCase().includes(search.trim().toLowerCase())).map(item => {
                        const selected = picker === "country" ? form.country === item : picker === "main" && form.niches[0] === item;
                        return <button type="button" key={item} onClick={() => selectItem(item)} aria-pressed={Boolean(selected)} className={`flex min-h-12 w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left text-sm ${selected ? "bg-[#327691]/10 font-bold text-[#327691] dark:text-[#5FAFCB]" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}>{item}{selected && <LuCheck size={20} />}</button>;
                    })}
                    {!items.some(item => item.toLowerCase().includes(search.trim().toLowerCase())) && <p className="py-8 text-center text-sm text-slate-500">No matches found.</p>}
                </div>
            </ModalSheet>
            <ModalSheet open={viewPhoto} onClose={() => setViewPhoto(false)} title="Profile Photo"><Picture url={photoUrl} user={profile} className="mx-auto aspect-square w-full max-w-sm rounded-2xl object-cover" /></ModalSheet>
            {cropUrl && <ImageCropModal image={cropUrl} aspect={1} onCancel={() => setCropFile(null)} onCropComplete={async blob => {
                try { setPhoto(await resizeImage(blob, 400)); setCropFile(null); }
                catch { setError("Could not prepare your photo. Please try another image."); setCropFile(null); }
            }} />}
        </AppPage>
    );
}

